import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { PoAccrualResult, PoAccrualSnapshot } from "@ranfac/shared";
import { downloadPoAccrualJe, fetchPoAccrual } from "../api.js";
import { WorkpaperActions } from "../components/WorkpaperActions.js";

export function PoAccrual() {
  const { period = "2026-01" } = useParams();
  const periodEnd = periodEndOf(period);
  const [data, setData] = useState<{
    snapshot: PoAccrualSnapshot;
    result: PoAccrualResult;
    source: "upload" | "fixture";
  } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchPoAccrual(periodEnd)
      .then(setData)
      .catch((e) => setErr(String(e)));
  }, [periodEnd]);

  if (err) return <div className="banner warn">Could not load: {err}</div>;
  if (!data) return <div className="card">Loading…</div>;

  const { snapshot, result, source } = data;
  const varianceOk = Math.abs(result.varianceFromBook) < 0.01;

  async function onDownloadJe() {
    setBusy(true);
    try {
      const { filename, csv } = await downloadPoAccrualJe(periodEnd);
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      setToast(`Downloaded ${filename}. Import via Library Master → GL Import.`);
    } catch (e) {
      setToast(`JE generation failed: ${e}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="card">
        <div style={{ marginBottom: 8 }}>
          <Link to={`/close/${period}`}>&larr; back to close dashboard</Link>
        </div>
        <h1>Accrued Purchases (PO Accrual)</h1>
        <div className="subtle">
          GL 2250 · period ending {snapshot.periodEnd} · {result.lines.length}{" "}
          open PO receipts ·{" "}
          {source === "upload" ? (
            <span className="badge" style={{ background: "#dcfce7", color: "#166534" }}>
              uploaded file
            </span>
          ) : (
            <span className="badge warn">sample data — upload PO accrual to use live numbers</span>
          )}
        </div>

        <div className="meta" style={{ marginTop: 16 }}>
          <div className="item">
            <div className="label">Sage source</div>
            <div>
              PO_ReceiptHistoryHeader, PO_ReceiptHistoryDetail,
              PO_PurchaseOrderDetail
            </div>
          </div>
          <div className="item">
            <div className="label">Pulled via</div>
            <div>
              Cloudflare Tunnel · ProvideX ODBC{" "}
              <span className="badge warn">fixture (scaffold)</span>
            </div>
          </div>
          <div className="item">
            <div className="label">Phase</div>
            <div>1 · Week-1 prototype</div>
          </div>
        </div>
      </div>

      <div className="kpi-grid">
        <Kpi
          label="Computed accrual (per-line)"
          value={fmt(
            result.lines.reduce(
              (s, l) => s + l.computedAccrual,
              0,
            ),
          )}
        />
        <Kpi label="Stored (workpaper)" value={fmt(result.storedTotal)} />
        <Kpi label="GL 2250 book balance" value={fmt(result.bookBalanceGl2250)} />
        <Kpi
          label="Variance vs. book"
          value={fmt(result.varianceFromBook)}
          variant={varianceOk ? "ok" : "warn"}
        />
      </div>

      {result.linesWithAdjustments > 0 && (
        <div className="banner warn">
          {result.linesWithAdjustments} line
          {result.linesWithAdjustments === 1 ? " has" : "s have"} a preparer
          adjustment vs. the qty-derived calculation — highlighted in the table
          below. Reviewer should confirm before sign-off.
        </div>
      )}

      <div className="toolbar">
        <button onClick={onDownloadJe} disabled={busy || varianceOk}>
          Generate JE (CSV)
        </button>
      </div>

      <WorkpaperActions reconId="po-accrual" period={period} />

      <div className="card">
        <h2>Line items</h2>
        <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>PO #</th>
                <th>Vendor</th>
                <th>Item</th>
                <th>Prod Line</th>
                <th>GL</th>
                <th className="num">Ord</th>
                <th className="num">Rec'd</th>
                <th className="num">Inv'd</th>
                <th className="num">Unit $</th>
                <th className="num">Computed</th>
                <th className="num">Stored</th>
                <th className="num">Δ</th>
              </tr>
            </thead>
            <tbody>
              {result.lines.map((line, i) => (
                <tr
                  key={`${line.row.poNumber}-${line.row.itemNumber}-${i}`}
                  className={line.hasAdjustment ? "row-has-adj" : ""}
                >
                  <td className="monoish">{line.row.poNumber}</td>
                  <td>{line.row.vendorName}</td>
                  <td className="monoish">{line.row.itemNumber}</td>
                  <td>{line.row.productLine}</td>
                  <td className="monoish">{line.row.glAccount}</td>
                  <td className="num">{line.row.qtyOrdered}</td>
                  <td className="num">{line.row.qtyReceived}</td>
                  <td className="num">{line.row.qtyInvoiced}</td>
                  <td className="num">{line.row.unitCost.toFixed(4)}</td>
                  <td className="num">{fmt(line.computedAccrual)}</td>
                  <td className="num">{fmt(line.storedAccrual)}</td>
                  <td className="num">
                    {line.hasAdjustment ? fmt(line.adjustmentDelta) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {toast && (
        <div className="toast" onClick={() => setToast(null)}>
          {toast}
        </div>
      )}
    </>
  );
}

function Kpi({
  label,
  value,
  variant,
}: {
  label: string;
  value: string | number;
  variant?: "ok" | "warn";
}) {
  return (
    <div className={`kpi ${variant ? "variance" : ""}`}>
      <div className="label">{label}</div>
      <div className={`value ${variant ?? ""}`}>{value}</div>
    </div>
  );
}

function fmt(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function periodEndOf(period: string): string {
  const [y, m] = period.split("-");
  const year = Number(y);
  const month = Number(m);
  const last = new Date(Date.UTC(year, month, 0));
  return last.toISOString().slice(0, 10);
}
